import type { ConditionEngineRules, Condition } from './editor-types';

export interface StrategyTemplatePreset {
  id: string;
  name: string;
  description: string;
  capitalNote: string;
  defaultName: string;
  defaultDescription: string;
  rules: ConditionEngineRules;
}

// ─── Shared signal conditions (RSI-based) ─────────────

const SIGNAL_ENTRY_CONDITION: Condition = {
  type: 'COMPARISON',
  left: { type: 'PRICE', field: 'CURRENT' },
  op: '>',
  right: { type: 'CONSTANT', value: 0 },
};

const RSI_BUY_CONDITION: Condition = {
  type: 'COMPARISON',
  left: { type: 'INDICATOR', indicator: 'RSI', params: { period: 14 } },
  op: '<',
  right: { type: 'CONSTANT', value: 30 },
};

const RSI_SELL_CONDITION: Condition = {
  type: 'COMPARISON',
  left: { type: 'INDICATOR', indicator: 'RSI', params: { period: 14 } },
  op: '>',
  right: { type: 'CONSTANT', value: 70 },
};

// ─── Martingale Grid ──────────────────────────────────
// Signal-based entry (RSI 30/70), grid with conservative 1.3x martingale.
// Wider grid step ($8) and higher TP ($8) to clear friction costs.
// Fewer grid levels (6) to limit total exposure.

const MARTINGALE_GRID: ConditionEngineRules = {
  strategy_type: 'CONDITION_ENGINE',
  market_data: { timeframe: '5m' },
  entry: {
    rules: [
      {
        id: 'entry_1',
        direction: 'SIGNAL',
        conditions: structuredClone(SIGNAL_ENTRY_CONDITION),
        buyCondition: structuredClone(RSI_BUY_CONDITION),
        sellCondition: structuredClone(RSI_SELL_CONDITION),
        lotSizing: { type: 'FIXED', volume: 0.01 },
        maxPositions: 1,
      },
    ],
    grid: {
      enabled: true,
      direction: 'FOLLOW_ENTRY',
      stepType: 'ABSOLUTE',
      stepValue: 8,
      maxGridLevels: 6,
      lotSizing: { type: 'MARTINGALE', baseLotRiskFactor: 2, multiplier: 1.3 },
    },
  },
  exit: {
    rules: [
      {
        id: 'exit_1',
        name: 'Take Profit',
        conditions: {
          type: 'COMPARISON',
          left: { type: 'POSITION', field: 'FLOATING_PNL' },
          op: '>=',
          right: { type: 'CONSTANT', value: 8 },
        },
        action: 'CLOSE_ALL',
      },
    ],
    equityStopLossPct: 40,
  },
  risk: {
    riskFactor: 1,
    maxTotalPositions: 6,
  },
};

// ─── Reverse Martingale Grid ─────────────────────────
// Lot DECREASES each level (0.7x) — lower risk at deeper grid levels.
// Wider step ($10) and moderate TP ($6) for steady small gains.

const REVERSE_MARTINGALE_GRID: ConditionEngineRules = {
  strategy_type: 'CONDITION_ENGINE',
  market_data: { timeframe: '5m' },
  entry: {
    rules: [
      {
        id: 'entry_1',
        direction: 'SIGNAL',
        conditions: structuredClone(SIGNAL_ENTRY_CONDITION),
        buyCondition: structuredClone(RSI_BUY_CONDITION),
        sellCondition: structuredClone(RSI_SELL_CONDITION),
        lotSizing: { type: 'FIXED', volume: 0.01 },
        maxPositions: 1,
      },
    ],
    grid: {
      enabled: true,
      direction: 'FOLLOW_ENTRY',
      stepType: 'ABSOLUTE',
      stepValue: 10,
      maxGridLevels: 8,
      lotSizing: { type: 'MARTINGALE', baseLotRiskFactor: 2, multiplier: 0.7 },
    },
  },
  exit: {
    rules: [
      {
        id: 'exit_1',
        name: 'Take Profit',
        conditions: {
          type: 'COMPARISON',
          left: { type: 'POSITION', field: 'FLOATING_PNL' },
          op: '>=',
          right: { type: 'CONSTANT', value: 6 },
        },
        action: 'CLOSE_ALL',
      },
    ],
    equityStopLossPct: 35,
  },
  risk: {
    riskFactor: 1,
    maxTotalPositions: 8,
  },
};

// ─── Conservative Grid ───────────────────────────────
// Fixed lot, wide grid step ($12), higher TP ($5), only 5 levels.
// Designed for minimal exposure and steady small captures.

const CONSERVATIVE_GRID: ConditionEngineRules = {
  strategy_type: 'CONDITION_ENGINE',
  market_data: { timeframe: '15m' },
  entry: {
    rules: [
      {
        id: 'entry_1',
        direction: 'SIGNAL',
        conditions: structuredClone(SIGNAL_ENTRY_CONDITION),
        buyCondition: structuredClone(RSI_BUY_CONDITION),
        sellCondition: structuredClone(RSI_SELL_CONDITION),
        lotSizing: { type: 'FIXED', volume: 0.01 },
        maxPositions: 1,
      },
    ],
    grid: {
      enabled: true,
      direction: 'FOLLOW_ENTRY',
      stepType: 'ABSOLUTE',
      stepValue: 12,
      maxGridLevels: 5,
      lotSizing: { type: 'FIXED', volume: 0.01 },
    },
  },
  exit: {
    rules: [
      {
        id: 'exit_1',
        name: 'Take Profit',
        conditions: {
          type: 'COMPARISON',
          left: { type: 'POSITION', field: 'FLOATING_PNL' },
          op: '>=',
          right: { type: 'CONSTANT', value: 5 },
        },
        action: 'CLOSE_ALL',
      },
    ],
    equityStopLossPct: 30,
  },
  risk: {
    riskFactor: 1,
    maxTotalPositions: 5,
  },
};

// ─── MACD Crossover Trend ───────────────────────────
// MACD/Signal crossover with strong ADX filter (>30).
// 4h timeframe to reduce whipsaws. Wider trailing stop (3x ATR).
// Only 1 position at a time for controlled risk.

const MACD_CROSSOVER: ConditionEngineRules = {
  strategy_type: 'CONDITION_ENGINE',
  market_data: { timeframe: '4h' },
  entry: {
    rules: [
      {
        id: 'entry_macd',
        direction: 'SIGNAL',
        conditions: {
          type: 'COMPARISON',
          left: { type: 'INDICATOR', indicator: 'ADX', params: { period: 14 } },
          op: '>',
          right: { type: 'CONSTANT', value: 30 },
        },
        buyCondition: {
          type: 'COMPARISON',
          left: { type: 'INDICATOR', indicator: 'MACD', params: { period: 26, fastPeriod: 12, slowPeriod: 26, signalPeriod: 9 } },
          op: 'CROSSES_ABOVE',
          right: { type: 'INDICATOR', indicator: 'MACD_SIGNAL', params: { period: 26, fastPeriod: 12, slowPeriod: 26, signalPeriod: 9 } },
        },
        sellCondition: {
          type: 'COMPARISON',
          left: { type: 'INDICATOR', indicator: 'MACD', params: { period: 26, fastPeriod: 12, slowPeriod: 26, signalPeriod: 9 } },
          op: 'CROSSES_BELOW',
          right: { type: 'INDICATOR', indicator: 'MACD_SIGNAL', params: { period: 26, fastPeriod: 12, slowPeriod: 26, signalPeriod: 9 } },
        },
        lotSizing: { type: 'FIXED', volume: 0.01 },
        maxPositions: 1,
      },
    ],
  },
  exit: {
    rules: [
      {
        id: 'exit_reversal',
        name: 'Trend Reversal',
        conditions: {
          type: 'COMPARISON',
          left: { type: 'INDICATOR', indicator: 'ADX', params: { period: 14 } },
          op: '<',
          right: { type: 'CONSTANT', value: 20 },
        },
        action: 'CLOSE_ALL',
      },
    ],
    trailingStop: {
      enabled: true,
      type: 'ATR_MULTIPLE',
      value: 3,
      atrPeriod: 14,
    },
    equityStopLossPct: 20,
  },
  risk: {
    riskFactor: 1,
    maxTotalPositions: 1,
  },
};

// ─── London Session Scalper ─────────────────────────
// London session (8–16 UTC) with tight Stochastic extremes (15/85).
// Lower TP ($3) for faster exits. Cooldown 12 candles to avoid overtrading.
// Max 2 positions. Session close at 15:00 to avoid end-of-session slippage.

const LONDON_SESSION_SCALPER: ConditionEngineRules = {
  strategy_type: 'CONDITION_ENGINE',
  market_data: { timeframe: '5m' },
  entry: {
    rules: [
      {
        id: 'entry_buy',
        direction: 'BUY',
        conditions: {
          type: 'AND',
          conditions: [
            { type: 'COMPARISON', left: { type: 'TIME', field: 'HOUR' }, op: '>=', right: { type: 'CONSTANT', value: 8 } },
            { type: 'COMPARISON', left: { type: 'TIME', field: 'HOUR' }, op: '<', right: { type: 'CONSTANT', value: 15 } },
            { type: 'COMPARISON', left: { type: 'TIME', field: 'DAY_OF_WEEK' }, op: '>=', right: { type: 'CONSTANT', value: 1 } },
            { type: 'COMPARISON', left: { type: 'TIME', field: 'DAY_OF_WEEK' }, op: '<=', right: { type: 'CONSTANT', value: 5 } },
            { type: 'COMPARISON', left: { type: 'INDICATOR', indicator: 'STOCH_K', params: { period: 14, signalPeriod: 3 } }, op: '<', right: { type: 'CONSTANT', value: 15 } },
            { type: 'COMPARISON', left: { type: 'INDICATOR', indicator: 'STOCH_K', params: { period: 14, signalPeriod: 3 } }, op: 'CROSSES_ABOVE', right: { type: 'INDICATOR', indicator: 'STOCH_D', params: { period: 14, signalPeriod: 3 } } },
          ],
        },
        lotSizing: { type: 'FIXED', volume: 0.01 },
        cooldownCandles: 12,
        maxPositions: 2,
      },
      {
        id: 'entry_sell',
        direction: 'SELL',
        conditions: {
          type: 'AND',
          conditions: [
            { type: 'COMPARISON', left: { type: 'TIME', field: 'HOUR' }, op: '>=', right: { type: 'CONSTANT', value: 8 } },
            { type: 'COMPARISON', left: { type: 'TIME', field: 'HOUR' }, op: '<', right: { type: 'CONSTANT', value: 15 } },
            { type: 'COMPARISON', left: { type: 'TIME', field: 'DAY_OF_WEEK' }, op: '>=', right: { type: 'CONSTANT', value: 1 } },
            { type: 'COMPARISON', left: { type: 'TIME', field: 'DAY_OF_WEEK' }, op: '<=', right: { type: 'CONSTANT', value: 5 } },
            { type: 'COMPARISON', left: { type: 'INDICATOR', indicator: 'STOCH_K', params: { period: 14, signalPeriod: 3 } }, op: '>', right: { type: 'CONSTANT', value: 85 } },
            { type: 'COMPARISON', left: { type: 'INDICATOR', indicator: 'STOCH_K', params: { period: 14, signalPeriod: 3 } }, op: 'CROSSES_BELOW', right: { type: 'INDICATOR', indicator: 'STOCH_D', params: { period: 14, signalPeriod: 3 } } },
          ],
        },
        lotSizing: { type: 'FIXED', volume: 0.01 },
        cooldownCandles: 12,
        maxPositions: 2,
      },
    ],
  },
  exit: {
    rules: [
      {
        id: 'exit_tp',
        name: 'Take Profit',
        conditions: {
          type: 'COMPARISON',
          left: { type: 'POSITION', field: 'FLOATING_PNL' },
          op: '>=',
          right: { type: 'CONSTANT', value: 3 },
        },
        action: 'CLOSE_ALL',
      },
      {
        id: 'exit_session_end',
        name: 'Session Close',
        conditions: {
          type: 'COMPARISON',
          left: { type: 'TIME', field: 'HOUR' },
          op: '>=',
          right: { type: 'CONSTANT', value: 15 },
        },
        action: 'CLOSE_ALL',
      },
    ],
    equityStopLossPct: 20,
  },
  risk: {
    riskFactor: 1,
    maxTotalPositions: 4,
  },
};

// ─── CCI Mean Reversion ─────────────────────────────
// Deeper CCI extremes (-150/+150) for higher-probability entries.
// Williams %R confirms. Wider partial TP (1%) and full TP (2%).
// Longer cooldown (8 candles) to reduce overtrade.

const CCI_MEAN_REVERSION: ConditionEngineRules = {
  strategy_type: 'CONDITION_ENGINE',
  market_data: { timeframe: '15m' },
  entry: {
    rules: [
      {
        id: 'entry_buy',
        direction: 'BUY',
        conditions: {
          type: 'AND',
          conditions: [
            { type: 'COMPARISON', left: { type: 'INDICATOR', indicator: 'CCI', params: { period: 20 } }, op: '<', right: { type: 'CONSTANT', value: -150 } },
            { type: 'COMPARISON', left: { type: 'INDICATOR', indicator: 'WILLIAMS_R', params: { period: 14 } }, op: '<', right: { type: 'CONSTANT', value: -85 } },
          ],
        },
        lotSizing: { type: 'FIXED', volume: 0.01 },
        cooldownCandles: 8,
        maxPositions: 2,
      },
      {
        id: 'entry_sell',
        direction: 'SELL',
        conditions: {
          type: 'AND',
          conditions: [
            { type: 'COMPARISON', left: { type: 'INDICATOR', indicator: 'CCI', params: { period: 20 } }, op: '>', right: { type: 'CONSTANT', value: 150 } },
            { type: 'COMPARISON', left: { type: 'INDICATOR', indicator: 'WILLIAMS_R', params: { period: 14 } }, op: '>', right: { type: 'CONSTANT', value: -15 } },
          ],
        },
        lotSizing: { type: 'FIXED', volume: 0.01 },
        cooldownCandles: 8,
        maxPositions: 2,
      },
    ],
  },
  exit: {
    rules: [
      {
        id: 'exit_partial',
        name: 'Partial Take Profit',
        conditions: {
          type: 'COMPARISON',
          left: { type: 'POSITION', field: 'FLOATING_PNL_PCT' },
          op: '>=',
          right: { type: 'CONSTANT', value: 0.01 },
        },
        action: 'CLOSE_PARTIAL',
        closePct: 50,
      },
      {
        id: 'exit_full',
        name: 'Full Take Profit',
        conditions: {
          type: 'COMPARISON',
          left: { type: 'POSITION', field: 'FLOATING_PNL_PCT' },
          op: '>=',
          right: { type: 'CONSTANT', value: 0.02 },
        },
        action: 'CLOSE_ALL',
      },
      {
        id: 'exit_mean',
        name: 'Mean Reversion Exit',
        conditions: {
          type: 'AND',
          conditions: [
            { type: 'COMPARISON', left: { type: 'INDICATOR', indicator: 'CCI', params: { period: 20 } }, op: '>=', right: { type: 'CONSTANT', value: -20 } },
            { type: 'COMPARISON', left: { type: 'INDICATOR', indicator: 'CCI', params: { period: 20 } }, op: '<=', right: { type: 'CONSTANT', value: 20 } },
          ],
        },
        action: 'CLOSE_ALL',
      },
    ],
    equityStopLossPct: 20,
  },
  risk: {
    riskFactor: 1,
    maxTotalPositions: 4,
  },
};

// ─── Scaled Exit Trend Follower ─────────────────────
// EMA 20/50 crossover (slower, fewer whipsaws) with ADX > 25 trend filter.
// 4h timeframe for cleaner signals. 3x ATR trailing stop.
// Partial close at 1.5% profit, trail the rest.

const SCALED_EXIT_TREND: ConditionEngineRules = {
  strategy_type: 'CONDITION_ENGINE',
  market_data: { timeframe: '4h' },
  entry: {
    rules: [
      {
        id: 'entry_signal',
        direction: 'SIGNAL',
        conditions: {
          type: 'COMPARISON',
          left: { type: 'INDICATOR', indicator: 'ADX', params: { period: 14 } },
          op: '>',
          right: { type: 'CONSTANT', value: 25 },
        },
        buyCondition: {
          type: 'COMPARISON',
          left: { type: 'INDICATOR', indicator: 'EMA', params: { period: 20 } },
          op: 'CROSSES_ABOVE',
          right: { type: 'INDICATOR', indicator: 'EMA', params: { period: 50 } },
        },
        sellCondition: {
          type: 'COMPARISON',
          left: { type: 'INDICATOR', indicator: 'EMA', params: { period: 20 } },
          op: 'CROSSES_BELOW',
          right: { type: 'INDICATOR', indicator: 'EMA', params: { period: 50 } },
        },
        lotSizing: { type: 'FIXED', volume: 0.01 },
        maxPositions: 1,
      },
    ],
  },
  exit: {
    rules: [
      {
        id: 'exit_partial_1',
        name: 'Scale Out 50%',
        conditions: {
          type: 'COMPARISON',
          left: { type: 'POSITION', field: 'FLOATING_PNL_PCT' },
          op: '>=',
          right: { type: 'CONSTANT', value: 0.015 },
        },
        action: 'CLOSE_PARTIAL',
        closePct: 50,
      },
      {
        id: 'exit_trend_loss',
        name: 'Trend Exhaustion',
        conditions: {
          type: 'COMPARISON',
          left: { type: 'INDICATOR', indicator: 'ADX', params: { period: 14 } },
          op: '<',
          right: { type: 'CONSTANT', value: 18 },
        },
        action: 'CLOSE_ALL',
      },
    ],
    trailingStop: {
      enabled: true,
      type: 'ATR_MULTIPLE',
      value: 3,
      atrPeriod: 14,
    },
    equityStopLossPct: 15,
  },
  risk: {
    riskFactor: 1,
    maxTotalPositions: 1,
  },
};

// ─── From Scratch ────────────────────────────────────
// Minimal default: one entry rule, one exit rule, no grid.

const FROM_SCRATCH: ConditionEngineRules = {
  strategy_type: 'CONDITION_ENGINE',
  market_data: { timeframe: '15m' },
  entry: {
    rules: [
      {
        id: 'entry_1',
        direction: 'BUY',
        conditions: { type: 'AND', conditions: [] },
        lotSizing: { type: 'FIXED', volume: 0.01 },
      },
    ],
  },
  exit: {
    rules: [
      {
        id: 'exit_1',
        name: '',
        conditions: { type: 'AND', conditions: [] },
        action: 'CLOSE_ALL',
      },
    ],
    equityStopLossPct: 100,
  },
  risk: {
    riskFactor: 1,
    maxTotalPositions: 10,
  },
};

// ─── Template Registry ───────────────────────────────

export const STRATEGY_TEMPLATES: StrategyTemplatePreset[] = [
  {
    id: 'from-scratch',
    name: 'From Scratch',
    description: 'Start with a blank template. Full control over all rules.',
    capitalNote: 'Depends on your rules. Consider lot sizes, max positions, and your symbol\'s margin requirements.',
    defaultName: '',
    defaultDescription: '',
    rules: FROM_SCRATCH,
  },
  {
    id: 'martingale-grid',
    name: 'Martingale Grid',
    description: 'Scale up lot size at each grid level (1.3x). Wide $8 steps with $8 TP target.',
    capitalNote: '$500–$2,000 recommended. Grid opens up to 6 positions with increasing lot sizes — needs headroom for drawdowns.',
    defaultName: 'Martingale Grid Strategy',
    defaultDescription: 'Grid strategy with 1.3x martingale lot scaling per level. RSI 30/70 signal entry, $8 grid steps, closes all on $8 basket profit. Conservative 6-level grid limits total exposure.',
    rules: MARTINGALE_GRID,
  },
  {
    id: 'reverse-martingale-grid',
    name: 'Reverse Martingale Grid',
    description: 'Decrease lot size at each grid level (0.7x). Lower risk per level, wide $10 steps.',
    capitalNote: '$300–$1,000 recommended. Decreasing lots reduce exposure at deeper levels, 8 levels with $10 spacing.',
    defaultName: 'Reverse Martingale Grid Strategy',
    defaultDescription: 'Grid strategy with 0.7x reverse martingale lot scaling. Each grid level opens a smaller position than the previous. $10 grid steps and $6 TP for steady small gains.',
    rules: REVERSE_MARTINGALE_GRID,
  },
  {
    id: 'conservative-grid',
    name: 'Conservative Grid',
    description: 'Fixed lot at every grid level. Wide $12 steps, only 5 levels, 15m timeframe.',
    capitalNote: '$200–$500 recommended. Fixed 0.01 lot at every level — lowest margin requirement of all grid templates.',
    defaultName: 'Conservative Grid Strategy',
    defaultDescription: 'Grid strategy with fixed lot sizing at every level. $12 grid steps on 15m timeframe, only 5 levels for minimal exposure. Designed for steady small captures.',
    rules: CONSERVATIVE_GRID,
  },
  {
    id: 'macd-crossover',
    name: 'MACD Crossover Trend',
    description: 'MACD/Signal crossover on 4h with strong ADX filter (>30) and 3x ATR trailing.',
    capitalNote: '$200–$500 recommended. Fixed 0.01 lot, 1 position max — low capital needs.',
    defaultName: 'MACD Crossover Trend Strategy',
    defaultDescription: 'Enters on MACD crossing its signal line on 4h timeframe when ADX > 30 confirms strong trend. 3x ATR trailing stop rides trends. Exits when trend weakens (ADX < 20).',
    rules: MACD_CROSSOVER,
  },
  {
    id: 'london-session-scalper',
    name: 'London Session Scalper',
    description: 'Stochastic scalper with tight extremes (15/85), London hours only, 12-candle cooldown.',
    capitalNote: '$100–$300 recommended. Fixed 0.01 lot, max 4 positions — low capital needs. Best for forex pairs.',
    defaultName: 'London Session Scalper',
    defaultDescription: 'Trades only during London session (8:00–15:00 UTC, weekdays). Uses Stochastic K/D crossovers at 15/85 extremes. $3 TP target, closes all at session end. 12-candle cooldown prevents overtrading.',
    rules: LONDON_SESSION_SCALPER,
  },
  {
    id: 'cci-mean-reversion',
    name: 'CCI Mean Reversion',
    description: 'Deep CCI extremes (±150) with Williams %R confirmation. Partial + full TP.',
    capitalNote: '$200–$500 recommended. Fixed 0.01 lot, max 4 positions — partial closes reduce exposure early.',
    defaultName: 'CCI Mean Reversion Strategy',
    defaultDescription: 'Enters when CCI hits ±150 extremes confirmed by Williams %R. Partial closes 50% at 1% profit, full close at 2% or when CCI returns to neutral. 8-candle cooldown filters noise.',
    rules: CCI_MEAN_REVERSION,
  },
  {
    id: 'scaled-exit-trend',
    name: 'Scaled Exit Trend Follower',
    description: 'EMA 20/50 crossover on 4h with ADX > 25. Scale out 50% at 1.5%, trail the rest.',
    capitalNote: '$200–$500 recommended. Fixed 0.01 lot, 1 position max — clean trend following.',
    defaultName: 'Scaled Exit Trend Follower',
    defaultDescription: 'EMA 20/50 crossover entry on 4h when ADX > 25 confirms trend. Scales out 50% at 1.5% profit, then trails remainder with 3x ATR trailing stop. Exits fully when trend exhausts (ADX < 18).',
    rules: SCALED_EXIT_TREND,
  },
];
